export type HiringLevel = "Senior IC / Principal PM" | "Head of Product" | "VP of Product" | "CPO";
export type Tilt = 0 | 1 | 2 | 3;

export type DecisionQuestion = {
  id: string;
  dimension: string;
  question: string;
  weight: number;
  options: Array<{ tilt: Tilt; text: string; short: string }>;
};

export const decisionQuestions: DecisionQuestion[] = [
  { id: "urgency", dimension: "Urgency and ramp time", question: "How soon do you need product leadership in place?", weight: 1.2, options: [
    { tilt: 3, text: "We need outcomes or fixes within 14 to 30 days.", short: "Within 30 days" },
    { tilt: 2, text: "We need someone in place within about 6 to 8 weeks.", short: "Within 6 to 8 weeks" },
    { tilt: 1, text: "We can wait about 3 months for a search and a notice period.", short: "Within 3 months" },
    { tilt: 0, text: "We can wait 3 to 6 months or more, and want the right long term hire.", short: "3 to 6 months or more" },
  ] },
  { id: "scope", dimension: "Workload scope", question: "What does the role actually need to deliver?", weight: 1.4, options: [
    { tilt: 3, text: "A targeted strategic mandate, such as a pricing overhaul, a PLG pivot or a 0 to 1 launch.", short: "Targeted strategic mandate" },
    { tilt: 2, text: "A defined set of strategic priorities for the next 6 to 12 months.", short: "Defined priorities" },
    { tilt: 1, text: "A broad leadership role with some team management.", short: "Broad leadership" },
    { tilt: 0, text: "An ongoing, multi-year operating leadership role with a large team to manage.", short: "Multi-year operating role" },
  ] },
  { id: "delegation", dimension: "Founder delegation", question: "How much of product are you ready to hand over?", weight: 1.0, options: [
    { tilt: 3, text: "I want to keep strategic oversight and add part-time executive guidance.", short: "Keep strategic oversight" },
    { tilt: 2, text: "I will hand over some areas, but I will stay close to the roadmap.", short: "Hand over some areas" },
    { tilt: 1, text: "I want to hand over most of the daily product management.", short: "Hand over most" },
    { tilt: 0, text: "I am ready to hand over full daily management of product.", short: "Hand over everything daily" },
  ] },
  { id: "runway", dimension: "Runway and cash velocity", question: "What is your cash position?", weight: 1.1, options: [
    { tilt: 3, text: "We are conserving cash and extending runway between funding rounds.", short: "Conserving cash" },
    { tilt: 2, text: "Cash is tight, and every fixed cost is scrutinised.", short: "Cash is tight" },
    { tilt: 1, text: "We are funded, but we add fixed costs carefully.", short: "Funded, careful" },
    { tilt: 0, text: "We are fully capitalised and can carry a high annual fixed run-rate.", short: "Fully capitalised" },
  ] },
  { id: "team", dimension: "Team maturity", question: "What does your product team need most?", weight: 1.0, options: [
    { tilt: 3, text: "Our PMs are capable and need strategic direction and coaching.", short: "Strategic direction" },
    { tilt: 2, text: "The team is mostly capable, with gaps in senior judgement.", short: "Senior judgement" },
    { tilt: 1, text: "The team lacks some execution capacity.", short: "Some execution gaps" },
    { tilt: 0, text: "The organisation lacks execution capacity and needs a full-time operational anchor.", short: "Operational anchor" },
  ] },
  { id: "certainty", dimension: "Role certainty", question: "How clear is the long term role?", weight: 1.0, options: [
    { tilt: 3, text: "We are not sure of the scope, and want to test and de-risk the mandate first.", short: "Test and de-risk" },
    { tilt: 2, text: "The scope is fairly clear, but it may change within six months.", short: "Clear but may change" },
    { tilt: 1, text: "The scope is mostly clear.", short: "Mostly clear" },
    { tilt: 0, text: "The long term scope and the full-time spec are crystal clear.", short: "Crystal clear" },
  ] },
];

export const displayOrders = [[2, 0, 3, 1], [1, 3, 0, 2], [0, 2, 1, 3], [3, 1, 2, 0]];
export const levels: Record<HiringLevel, { dayRate: number; dayMin: number; dayMax: number; salary: number; salaryMin: number; salaryMax: number }> = {
  "Senior IC / Principal PM": { dayRate: 700, dayMin: 600, dayMax: 800, salary: 125000, salaryMin: 110000, salaryMax: 140000 },
  "Head of Product": { dayRate: 1000, dayMin: 800, dayMax: 1200, salary: 160000, salaryMin: 140000, salaryMax: 180000 },
  "VP of Product": { dayRate: 1250, dayMin: 1000, dayMax: 1500, salary: 205000, salaryMin: 180000, salaryMax: 230000 },
  CPO: { dayRate: 2000, dayMin: 1500, dayMax: 2500, salary: 290000, salaryMin: 230000, salaryMax: 350000 },
};

export type DecisionResult = {
  fractionalPercent: number;
  permanentPercent: number;
  outcome: string;
  outcomeText: string;
  strongestFractional: DecisionQuestion;
  strongestPermanent: DecisionQuestion;
};
export function calculateDecision(answers: Record<string, Tilt>): DecisionResult {
  const totalWeight = decisionQuestions.reduce((sum, q) => sum + q.weight, 0);
  const weighted = decisionQuestions.reduce((sum, q) => sum + q.weight * (answers[q.id] ?? 0), 0);
  const fractionalPercent = Math.round(weighted / (totalWeight * 3) * 100);
  const outcome = fractionalPercent >= 72 ? "Strongly Favors Fractional" : fractionalPercent >= 56 ? "Leaning Fractional (Bridge / Test Phase)" : fractionalPercent >= 36 ? "Balanced / Hybrid Approach" : "Requires Permanent Full-Time Executive";
  const outcomeText = fractionalPercent >= 72
    ? "Your needs point strongly to a fractional leader: you need results quickly, the mandate is targeted, and you want to protect cash."
    : fractionalPercent >= 56
      ? "Your needs lean fractional. Use a fractional leader as a bridge or a test phase while you firm up the scope of a permanent role."
      : fractionalPercent >= 36
        ? "Your needs are split. Start with a fractional bridge to cover the urgent work while you scope and search for a permanent hire in parallel."
        : "Your needs point to a permanent executive: a broad, long term role with a team to build and a founder ready to hand over.";
  const strongestFractional = decisionQuestions.reduce((best, q) => q.weight * (answers[q.id] ?? 0) > best.weight * (answers[best.id] ?? 0) ? q : best, decisionQuestions[0]);
  const strongestPermanent = decisionQuestions.reduce((best, q) => q.weight * (3 - (answers[q.id] ?? 0)) > best.weight * (3 - (answers[best.id] ?? 0)) ? q : best, decisionQuestions[0]);
  return { fractionalPercent, permanentPercent: 100 - fractionalPercent, outcome, outcomeText, strongestFractional, strongestPermanent };
}

export type CostInputs = { dayRate: number; days: number; months: number; base: number; feePercent: number; onCostPercent: number; burn: number };
export function calculateCosts(input: CostInputs) {
  const fracMonthly = input.dayRate * input.days * 4;
  const fracTotal = fracMonthly * input.months;
  const salaryCost = input.base * (1 + input.onCostPercent / 100);
  const recruiterFee = input.base * input.feePercent / 100;
  const fteYear1 = salaryCost + recruiterFee;
  const fteMonthly = salaryCost / 12;
  const saving = fteYear1 - fracTotal;
  const savingPercent = saving / fteYear1 * 100;
  const fteSameWindow = fteMonthly * input.months + recruiterFee;
  const sameWindowDiff = fteSameWindow - fracTotal;
  const crossoverMonths = fracMonthly > fteMonthly ? recruiterFee / (fracMonthly - fteMonthly) : null;
  const breakEvenDays12 = fteYear1 / (input.dayRate * 4 * 12);
  const runwayMonths = input.burn > 0 ? saving / input.burn : null;
  return { fracMonthly, fracTotal, salaryCost, recruiterFee, fteYear1, fteMonthly, saving, savingPercent, fteSameWindow, sameWindowDiff, crossoverMonths, breakEvenDays12, runwayMonths };
}

export function money(value: number) { return new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP", maximumFractionDigits: 0 }).format(value); }
export function pct(value: number) { return `${Math.round(value)}%`; }
export function rating(value: "low" | "medium" | "high") { return value === "low" ? "Low risk" : value === "medium" ? "Medium risk" : "High risk"; }
